/**
 * Display helpers for e-sign field labels shown to counterparties.
 * Template / Edit-fields UIs sometimes persist machine ids (Party2-details,
 * PartyB-Company); the public sign page must not show those raw.
 */
const KNOWN_LABELS = {
    "party2-details": "Your company details",
    party2details: "Your company details",
    "partyb-details": "Your company details",
    "partyb-company": "Company",
    partybcompany: "Company",
    "partyb-name": "Full name",
    partybname: "Full name",
    "partyb-title": "Title / capacity",
    partybtitle: "Title / capacity",
    "partyb-address": "Address",
    partybaddress: "Address",
    "partyb-datesigned": "Date signed",
    partybdatesigned: "Date signed",
    "partyb-date": "Date signed",
    "partyb-signature": "Signature",
    partybsignature: "Signature",
    "partya-signature": "Latitude30 signature",
    "party2-signature": "Signature",
};
/** Labels that look like form/control ids rather than human copy. */
export function isMachineSignatureFieldLabel(label) {
    const t = label.trim();
    if (!t)
        return true;
    if (/\s/.test(t))
        return false;
    if (/[-_]/.test(t))
        return true;
    if (/^(Party|Signer|Field|Text|Date)\d/i.test(t))
        return true;
    if (/^(Party[AB]|Party\d)/i.test(t) && /[A-Z]/.test(t.slice(1)))
        return true;
    return false;
}
function titleCaseWords(words) {
    return words
        .filter(Boolean)
        .map((w) => {
        const lower = w.toLowerCase();
        if (lower === "b")
            return "B";
        if (lower === "a")
            return "A";
        if (lower === "nda")
            return "NDA";
        return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
        .join(" ");
}
/**
 * Turn a stored template label into counterparty-friendly copy.
 * Already-human labels (spaces, managed-template phrasing) pass through.
 */
export function humanizeSignatureFieldLabel(label, fieldType) {
    const raw = (label ?? "").trim();
    if (!raw) {
        switch (fieldType) {
            case "signature":
                return "Signature";
            case "initials":
                return "Initials";
            case "date":
                return "Date";
            default:
                return "Details";
        }
    }
    if (!isMachineSignatureFieldLabel(raw)) {
        // Strip redundant party prefixes from otherwise-human labels
        // ("Party B signature" → "Signature") so aria/smoke stay stable.
        const cleaned = raw
            .replace(/^Party\s*B\s+/i, "")
            .replace(/^Party\s*A\s+/i, "Latitude30 ")
            .trim();
        if (!cleaned)
            return humanizeSignatureFieldLabel(null, fieldType);
        if (cleaned === raw)
            return raw;
        if (/^details$/i.test(cleaned))
            return "Your company details";
        if (/^company$/i.test(cleaned))
            return "Company";
        if (/^(?:legal\s+)?name$/i.test(cleaned))
            return "Legal name";
        if (/^legal\s+entity\s+name$/i.test(cleaned))
            return "Legal entity name";
        if (/^title$/i.test(cleaned))
            return "Title / capacity";
        if (/^(?:registered\s+)?address$/i.test(cleaned))
            return "Registered address";
        if (/^signature$/i.test(cleaned))
            return "Signature";
        if (/^initials$/i.test(cleaned))
            return "Initials";
        if (/^date(?:\s+signed)?$/i.test(cleaned))
            return "Date signed";
        return titleCaseWords(cleaned.split(/\s+/));
    }
    const key = raw.toLowerCase().replace(/[\s_]+/g, "-");
    const compact = key.replace(/-/g, "");
    if (KNOWN_LABELS[key])
        return KNOWN_LABELS[key];
    if (KNOWN_LABELS[compact])
        return KNOWN_LABELS[compact];
    const spaced = raw
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .replace(/[-_]+/g, " ")
        .replace(/\bParty\s*2\b/gi, "Party B")
        .replace(/\bParty\s*B\b/gi, "Party B")
        .replace(/\bParty\s*A\b/gi, "Party A")
        .replace(/\bDateSigned\b/gi, "Date signed")
        .replace(/\bdetails\b/gi, "details")
        .trim();
    let friendly = titleCaseWords(spaced.split(/\s+/));
    // Counterparty-facing: "Party B …" → drop the party prefix when redundant.
    friendly = friendly.replace(/^Party B\s+/i, "").replace(/^Party A\s+/i, "Latitude30 ");
    if (!friendly) {
        return humanizeSignatureFieldLabel(null, fieldType);
    }
    // Prefer "Your …" for generic details blocks on page 1.
    if (/^details$/i.test(friendly))
        return "Your company details";
    if (/^company$/i.test(friendly))
        return "Company";
    if (/^name$/i.test(friendly))
        return "Full name";
    if (/^title$/i.test(friendly))
        return "Title / capacity";
    return friendly;
}
/**
 * Stub / watermark text that appears in Word shells and must not count as a
 * completed signature field value.
 */
const JUNK_FIELD_VALUES = new Set([
    "companyname-address",
    "companyname address",
    "company name-address",
    "company name address",
    "[company]",
    "[name]",
    "[title]",
    "[address]",
    "companyname",
    "partyb",
    "party b",
    "tbd",
    "n/a",
    "-",
    "—",
].map((s) => s.toLowerCase()));
/** True when the signer has not yet provided a real value. */
export function isBlankSignatureFieldValue(value, placeholder) {
    const v = (value ?? "").trim();
    if (!v)
        return true;
    const lower = v.toLowerCase().replace(/\s+/g, " ");
    if (JUNK_FIELD_VALUES.has(lower))
        return true;
    if (JUNK_FIELD_VALUES.has(lower.replace(/\s/g, "-")))
        return true;
    const ph = (placeholder ?? "").trim().toLowerCase();
    if (ph && lower === ph)
        return true;
    // Camel/Pascal stubs like CompanyName-Address
    if (/^[A-Z][a-z]+(?:[A-Z][a-z]+)+(?:-[A-Z][a-z]+)*$/.test(v) && v.length < 40) {
        return true;
    }
    return false;
}
const STUB_PHRASE_PATTERNS = [
    /company\s*name\s*[-–—]?\s*address/i,
    /companyname[-–—]?address/i,
    /\[\s*company\s*\]/i,
    /\[\s*name\s*\]/i,
    /\[\s*title\s*\]/i,
    /\[\s*address\s*\]/i,
];
/**
 * Scan free text (PDF/Word extract) for counterparty identity stubs that must
 * not appear on a signable cover/parties block.
 * Returns distinctive hit labels for error messages / reports.
 */
export function findIdentityStubHits(text) {
    const hits = [];
    for (const re of STUB_PHRASE_PATTERNS) {
        const m = text.match(re);
        if (m?.[0])
            hits.push(m[0].trim());
    }
    return [...new Set(hits)];
}
/** Cover copy for Party B during authoring (role only — identity at sign). */
export const PARTY_B_COVER_ROLE_LINE = "Party B (counterparty)";
export const PARTY_B_COVER_INSTRUCTION = "To be completed by Party B when signing (legal name and address).";
/** Example / placeholder for signature date fields — unambiguous en-GB long form. */
export const SIGNATURE_DATE_PLACEHOLDER = "1 September 2026";
const MONTH_NAME_TO_NUMBER = {
    january: 1,
    february: 2,
    march: 3,
    april: 4,
    may: 5,
    june: 6,
    july: 7,
    august: 8,
    september: 9,
    october: 10,
    november: 11,
    december: 12,
    jan: 1,
    feb: 2,
    mar: 3,
    apr: 4,
    jun: 6,
    jul: 7,
    aug: 8,
    sep: 9,
    sept: 9,
    oct: 10,
    nov: 11,
    dec: 12,
};
/**
 * Display an ISO date (YYYY-MM-DD) for signing — never DD/MM/YYYY (ambiguous).
 */
export function formatSignatureDateValue(value) {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
    if (!match)
        return value;
    const date = new Date(`${match[1]}-${match[2]}-${match[3]}T12:00:00Z`);
    if (Number.isNaN(date.getTime()))
        return value;
    return date.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
    });
}
/** Parse ISO or long en-GB text (`1 September 2026`) into YYYY-MM-DD. */
export function parseSignatureDateInput(input) {
    const trimmed = input.trim();
    if (!trimmed)
        return null;
    const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
    if (iso) {
        const date = new Date(`${iso[1]}-${iso[2]}-${iso[3]}T12:00:00Z`);
        if (Number.isNaN(date.getTime()))
            return null;
        return trimmed;
    }
    const long = /^(\d{1,2})\s+([A-Za-z]+)\s+(\d{4})$/.exec(trimmed);
    if (long) {
        const day = Number(long[1]);
        const month = MONTH_NAME_TO_NUMBER[long[2].toLowerCase()];
        const year = Number(long[3]);
        if (!month || day < 1 || day > 31 || year < 1000)
            return null;
        const mm = String(month).padStart(2, "0");
        const dd = String(day).padStart(2, "0");
        const candidate = `${year}-${mm}-${dd}`;
        const date = new Date(`${candidate}T12:00:00Z`);
        if (Number.isNaN(date.getTime()))
            return null;
        return candidate;
    }
    return null;
}
/** Value shown in date text inputs — formatted when stored ISO is valid. */
export function signatureDateInputDisplay(stored) {
    const parsed = parseSignatureDateInput(stored);
    if (parsed)
        return formatSignatureDateValue(parsed);
    return stored;
}
const OFFICE_OR_PDF_EXT = /\.(docx|doc|xlsx|xls|pptx|ppt|pdf)$/i;
/** Trailing `(signing)`, optionally followed by a SharePoint collision suffix (` 1`). */
const TRAILING_SIGNING = /\s*\(signing\)(?:\s+\d+)?\s*$/i;
/**
 * Strip Office/PDF extensions and any trailing `(signing)` suffixes (repeat until stable).
 * Handles accidental stems like `Agreement (signing).docx (signing)` and
 * SharePoint renames like `Agreement (signing) 1.docx`.
 */
export function normalizeAgreementBasename(name) {
    let base = name.trim();
    for (;;) {
        const next = base.replace(OFFICE_OR_PDF_EXT, "").replace(TRAILING_SIGNING, "").trim();
        if (next === base)
            break;
        base = next;
    }
    return base.length > 0 ? base : "Agreement";
}
/**
 * Working-doc title for a signing shell — negotiated basename only.
 * Never appends `(signing)`; strips any leftover suffix from a prior draft.
 */
export function signingDraftTitle(sourceTitle) {
    return normalizeAgreementBasename(sourceTitle).slice(0, 500);
}
/**
 * Vault PDF filename from a working-doc name — negotiated basename only (no `(signing)`).
 */
export function pdfFilenameFrom(filename) {
    const base = normalizeAgreementBasename(filename);
    return `${base.length > 0 ? base : "document"}.pdf`;
}
//# sourceMappingURL=signature-field-labels.js.map